// "New roles for you" on Home: fresh postings from Greenhouse, Lever and
// Ashby boards, matched to the user by the backend (backend/new_roles.py,
// GET /roles/feed, fetched in newRolesApi.ts). This file splits them into time windows and builds the
// docs the row's buttons write.
//
//   users/{uid}/roleActions/{roleId}  { status: "saved" | "dismissed", company, title, ... , at }
//   users/{uid}/emails/feed_{roleId}  the application card "Applied" creates

import { normalizeCompany } from "./dreamCompanies";

/** A matched role, best first (GET /roles/feed). */
export type FeedRole = {
  /** "greenhouse:4012345", also the roleActions doc id */
  id: string;
  company: string;
  domain?: string | null;
  logo?: string | null;
  title: string;
  /** "Toronto, ON; New York" */
  location?: string | null;
  remote?: boolean;
  url: string;
  provider: string;
  /** ISO: when it counts as posted (backend new_roles.listed_at) */
  listedAt: string;
  score: number;
  /** One of their Dream Companies */
  dream?: boolean;
  /** "Future openings", "Apply anyway": sorted last */
  evergreen?: boolean;
  /** For See all's filters (backend new_roles.role_facets): the level its title says */
  level?: "intern" | "new_grad" | null;
  /** The user's target roles it fits */
  targets?: string[];
  /** The user's locations it's in */
  places?: string[];
  /** Its location in one of the user's places (backend shown_location), when it lists several */
  here?: string | null;
};

export type RoleActionStatus = "saved" | "dismissed";

/** users/{uid}/roleActions/{roleId}. Saved ones keep enough to show on the Dream Company page. */
export type RoleAction = {
  id: string;
  status: RoleActionStatus;
  company: string;
  title: string;
  url?: string;
  location?: string | null;
  provider?: string;
  listedAt?: string;
  /** For the company's icon on Drafts */
  domain?: string | null;
  logo?: string | null;
  /** Dismissed: why, if they said (the toast's "Why?") */
  reason?: "company" | "role" | "applied";
  at?: any;
};

export type RoleWindow = "today" | "3days" | "week" | "month" | "all";
/** "new": Home's card, the last week (GET /roles/feed). "all": See all, every open role (?scope=all). */
export type RoleScope = "new" | "all";

const HOUR_MS = 3600000;
const DAY_MS = 24 * HOUR_MS;

export const ROLE_WINDOWS: { id: RoleWindow; label: string; ms: number; empty: string }[] = [
  // The last 24 hours, so a role from last night still counts at 8am
  { id: "today", label: "Today", ms: DAY_MS, empty: "Nothing new today." },
  { id: "3days", label: "3 days", ms: 3 * DAY_MS, empty: "Nothing in the last 3 days." },
  { id: "week", label: "Week", ms: 7 * DAY_MS, empty: "No new roles this week." },
];

/** See all: every open role that fits, of any age */
export const ALL_WINDOWS: typeof ROLE_WINDOWS = [
  ...ROLE_WINDOWS,
  { id: "month", label: "Month", ms: 30 * DAY_MS, empty: "Nothing in the last month." },
  { id: "all", label: "All", ms: Infinity, empty: "No open roles fit you yet." },
];

export const windowsFor = (scope: RoleScope) => (scope === "all" ? ALL_WINDOWS : ROLE_WINDOWS);

/** A window needs this many roles to be picked by default */
export const DEFAULT_WINDOW_MIN = 3;
/** Rows on the Home card */
export const ROLE_ROWS = 5;

const listedTime = (role: FeedRole) => Date.parse(role.listedAt) || 0;

/** The roles listed within the window, best first: score, then newest; evergreen postings last. */
export function inWindow(roles: FeedRole[], window: RoleWindow, now = Date.now()): FeedRole[] {
  const ms = ALL_WINDOWS.find((w) => w.id === window)!.ms;
  return roles
    .filter((role) => now - listedTime(role) <= ms)
    .sort(
      (a, b) =>
        Number(!!a.evergreen) - Number(!!b.evergreen) || b.score - a.score || listedTime(b) - listedTime(a),
    );
}

export type RoleCounts = Partial<Record<RoleWindow, number>>;

export function windowCounts(roles: FeedRole[], now = Date.now(), windows = ROLE_WINDOWS): RoleCounts {
  const counts: RoleCounts = {};
  for (const w of windows) counts[w.id] = roles.filter((role) => now - listedTime(role) <= w.ms).length;
  return counts;
}

/**
 * Each window's count: counted from the roles, unless the backend had more
 * matches than it sent (`capped`). Then it's the backend's count less the
 * roles hidden since. A copy saved before `capped` existed: every window
 * with a backend count is taken as capped.
 *
 * Counting the roles keeps a window in step as its oldest roles age out of
 * it ("Showing the best 106 of 107" when one crossed the 7 day line).
 */
export function shownCounts(
  visible: FeedRole[],
  hidden: FeedRole[],
  server: RoleCounts | undefined,
  now = Date.now(),
  windows = ROLE_WINDOWS,
  capped?: RoleWindow[],
): RoleCounts {
  const here = windowCounts(visible, now, windows);
  if (!server) return here;
  const gone = windowCounts(hidden, now, windows);
  const counts: RoleCounts = {};
  for (const w of windows) {
    counts[w.id] =
      capped && !capped.includes(w.id) ? here[w.id] : Math.max((server[w.id] ?? 0) - (gone[w.id] ?? 0), 0);
  }
  return counts;
}

/**
 * Today, unless it has fewer than 3: then the smallest window that has 3 or
 * more. None does: the smallest window with the most there is.
 */
export function defaultWindow(counts: Partial<Record<RoleWindow, number>>): RoleWindow {
  const order: RoleWindow[] = ["today", "3days", "week"];
  const enough = order.find((w) => (counts[w] ?? 0) >= DEFAULT_WINDOW_MIN);
  if (enough) return enough;
  return order.find((w) => counts[w] === counts.week) ?? "today";
}

/** What to say when the window is empty, and the wider one to try. */
export function emptyWindow(window: RoleWindow, windows = ROLE_WINDOWS): { text: string; next: RoleWindow | null } {
  const i = windows.findIndex((w) => w.id === window);
  const next = windows[i + 1]?.id ?? null;
  return { text: windows[i].empty, next };
}

export const windowLabel = (window: RoleWindow) => ALL_WINDOWS.find((w) => w.id === window)!.label;

/** "just now", "35m ago", "2h ago", "3d ago", "5mo ago", "1y ago" */
export function postedAgo(iso: string, now = Date.now()): string {
  const mins = Math.max(0, Math.floor((now - (Date.parse(iso) || now)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** The first place it lists, or "Remote" */
const allLocations = (role: Pick<FeedRole, "location">) =>
  (role.location || "").split(";").map((s) => s.trim()).filter(Boolean);

/** One location: the one in the user's places (backend shown_location), else the first */
export function roleLocation(role: Pick<FeedRole, "location" | "remote" | "here">): string {
  return role.here || allLocations(role)[0] || (role.remote ? "Remote" : "");
}

/** roleLocation, with "+2" when the posting lists others too (the row under the title) */
function roleLocationLine(role: Pick<FeedRole, "location" | "remote" | "here">): string {
  const shown = roleLocation(role);
  const others = allLocations(role).length - 1;
  return shown && others > 0 ? `${shown} +${others}` : shown;
}

/** The muted line under the title: "Stripe · Toronto, ON · 2h ago" */
export function roleMeta(role: FeedRole, now = Date.now()): string {
  return [role.company, roleLocationLine(role), postedAgo(role.listedAt, now)].filter(Boolean).join(" · ");
}

/** yyyy-mm-dd in the user's time zone (what the tracker stores as `date`) */
export function localDate(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Doc id for the card "Applied" creates: one per role, so a double click can't make two */
export const appliedCardId = (role: FeedRole) => `feed_${role.id}`;

/**
 * The application card for "Applied" (same shape as useNewApplication in applicationShared.tsx).
 * Gmail sync merges the confirmation email into it later: same company,
 * same role, still at Applied (backend matching.choose_card). `date` never
 * changes, so it counts once toward the week's goal.
 */
export function applicationFromRole(role: FeedRole, now = new Date()) {
  return {
    message_id: appliedCardId(role),
    company: role.company,
    role: role.title,
    label: "submitted",
    score: 1.0,
    date: localDate(now),
    lctn: roleLocation(role),
    body: "Added from New roles",
    notes: "",
    manual_entry: true,
    source: "feed",
    feedRoleId: role.id,
    jobUrl: role.url,
    archiveId: null,
    history: [{ stage: "submitted", at: now.toISOString(), source: "seed" }],
  };
}

/** What a roleActions doc keeps about the role */
export function roleActionDoc(role: FeedRole, status: RoleActionStatus) {
  return {
    status,
    company: role.company,
    title: role.title,
    url: role.url,
    location: roleLocation(role) || null,
    provider: role.provider,
    listedAt: role.listedAt,
    domain: role.domain || null,
    logo: role.logo || null,
  };
}

/** Saved roles at this company, for its Dream Company page */
export function savedRolesFor(companyName: string, actions: RoleAction[]): RoleAction[] {
  const key = normalizeCompany(companyName);
  return key ? actions.filter((a) => a.status === "saved" && normalizeCompany(a.company) === key) : [];
}
